'use client';

import { clientAxios } from '@/shared/api';
import { LogLevel, logger } from '@/shared/utils';

interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FlipOptions {
  vertical: boolean;
  horizontal: boolean;
}

interface GetCroppedInterface {
  imageSrc: string;
  fileName?: string;
  flip?: FlipOptions;
  isExternalAiImage?: boolean;
  pixelCrop: PixelCrop;
}

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.src = url;
    image.setAttribute('crossOrigin', 'anonymous');

    const onLoad = () => {
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onError);
      resolve(image);
    };

    const onError = (error: Event) => {
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onError);
      reject(error);
    };

    image.addEventListener('load', onLoad);
    image.addEventListener('error', onError);
  });

export default async function getCroppedImg({
  fileName,
  isExternalAiImage,
  imageSrc,
  pixelCrop,
  flip = { horizontal: false, vertical: false },
}: GetCroppedInterface): Promise<File | null> {
  let finalUrl = imageSrc;
  let objectURLCreated = false;

  try {
    if (isExternalAiImage) {
      const { data: aiThumbNailData } = await clientAxios.get(
        '/collectionAiThumbNailProxyApi/',
        {
          params: {
            url: imageSrc,
          },
        }
      );
      const blob = new Blob([new Uint8Array(aiThumbNailData.data.data)], {
        type: 'image/jpeg',
      });
      finalUrl = URL.createObjectURL(blob);
      objectURLCreated = true;
    }

    const image = await createImage(finalUrl);

    // 더 이상 finalUrl이 필요하지 않으므로 revoke
    if (objectURLCreated) {
      URL.revokeObjectURL(finalUrl);
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const { width, height } = image;

    canvas.width = width;
    canvas.height = height;

    ctx.translate(width / 2, height / 2);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-width / 2, -height / 2);

    ctx.drawImage(image, 0, 0);

    const croppedImg = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height
    );

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(croppedImg, 0, 0);

    return new Promise((resolve, reject) => {
      canvas.toBlob((file) => {
        if (file) {
          const newFileName = `${fileName}-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.jpeg`;
          const newFile = new File([file], newFileName, { type: file.type });
          resolve(newFile);
        } else {
          reject(new Error('Failed to create blob from cropped canvas.'));
        }
      }, 'image/jpeg');
    });
  } catch (error) {
    logger(LogLevel.ERROR, error, {
      message: 'Error cropping image',
    });

    return null;
  } finally {
    if (objectURLCreated) {
      URL.revokeObjectURL(finalUrl);
    }
  }
}
